import { Box, Container, Typography, Button, Stack } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import HomeIcon from '@mui/icons-material/Home';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { motion } from 'framer-motion';

const NotFound = () => {
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        minHeight: 'calc(100vh - 80px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Background decoration */}
      <Box
        sx={{
          position: 'absolute',
          top: '-20%',
          right: '-10%',
          width: 600,
          height: 600,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(249, 115, 22, 0.08) 0%, transparent 70%)',
          filter: 'blur(80px)',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: '-20%',
          left: '-10%',
          width: 500,
          height: 500,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(8, 145, 178, 0.08) 0%, transparent 70%)',
          filter: 'blur(80px)',
        }}
      />

      <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Box sx={{ textAlign: 'center' }}>
            {/* Icon */}
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <Box
                sx={{
                  width: 120,
                  height: 120,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 4,
                  boxShadow: '0 20px 40px rgba(249, 115, 22, 0.3)',
                }}
              >
                <SearchOffIcon sx={{ fontSize: 60, color: 'white' }} />
              </Box>
            </motion.div>

            {/* 404 Text */}
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: '5rem', md: '8rem' },
                fontWeight: 900,
                background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                lineHeight: 1,
                mb: 2,
                letterSpacing: '-0.02em',
              }}
            >
              404
            </Typography>

            {/* Title */}
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                fontSize: { xs: '1.5rem', md: '2rem' },
                color: '#1e293b',
                mb: 2,
              }}
            >
              Sayfa Bulunamadı
            </Typography>

            {/* Description */}
            <Typography
              sx={{
                color: '#64748b',
                fontSize: { xs: '1rem', md: '1.1rem' },
                lineHeight: 1.7,
                maxWidth: 500,
                mx: 'auto',
                mb: 5,
              }}
            >
              Aradığınız sayfa mevcut değil veya taşınmış olabilir. Ana sayfaya giderek 
              aradığınızı bulabilirsiniz.
            </Typography>

            {/* Buttons */}
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              justifyContent="center"
            >
              <Button
                variant="contained"
                size="large"
                startIcon={<HomeIcon />}
                onClick={() => navigate('/')}
                sx={{
                  px: 4,
                  py: 1.5,
                  fontWeight: 700,
                  fontSize: '1rem',
                  background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
                  boxShadow: '0 4px 12px rgba(249, 115, 22, 0.3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #ea580c 0%, #c2410c 100%)',
                    boxShadow: '0 6px 16px rgba(249, 115, 22, 0.4)',
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                Ana Sayfaya Dön
              </Button>
              <Button
                variant="outlined"
                size="large"
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate(-1)}
                sx={{
                  px: 4,
                  py: 1.5,
                  fontWeight: 600,
                  fontSize: '1rem',
                  borderColor: '#cbd5e1',
                  color: '#64748b',
                  borderWidth: 2,
                  '&:hover': {
                    borderWidth: 2,
                    borderColor: '#94a3b8',
                    backgroundColor: 'rgba(100, 116, 139, 0.04)',
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                Geri Dön
              </Button>
            </Stack>

            {/* Popular Links */}
            <Box sx={{ mt: 6, pt: 4, borderTop: '1px solid #e2e8f0' }}>
              <Typography
                variant="body2"
                sx={{ color: '#94a3b8', mb: 2, fontWeight: 600 }}
              >
                Popüler Sayfalar
              </Typography>
              <Stack
                direction="row"
                spacing={3}
                justifyContent="center"
                flexWrap="wrap"
                gap={1}
              >
                {[
                  { label: 'Özellikler', path: '/' },
                  { label: 'Fiyatlandırma', path: '/fiyatlandirma' },
                  { label: 'İletişim', path: '/iletisim' },
                  { label: 'Blog', path: '/blog' },
                ].map((link, i) => (
                  <Button
                    key={i}
                    onClick={() => navigate(link.path)}
                    sx={{
                      color: '#64748b',
                      fontSize: '0.9rem',
                      fontWeight: 500,
                      textTransform: 'none',
                      '&:hover': {
                        color: '#f97316',
                        backgroundColor: 'transparent',
                      },
                    }}
                  >
                    {link.label}
                  </Button>
                ))}
              </Stack>
            </Box>
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};

export default NotFound;
